import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Film,
  Loader2,
  Play,
  Download,
  Trash2,
  Sparkles,
  RotateCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRESET_PROMPT = `Cinematic vertical 9:16. A black-haired woman in her early 30s, friendly Swiss European appearance, soft warm voice, professional but approachable. She stands inside a modern Swiss auto body shop with brightly lit polished concrete floor. She wears a clean black work polo. Behind her, a freshly painted glossy red sports car is visible, with soft cinematic lighting and shallow depth of field. Camera slowly pushes in. She speaks softly in German with a warm Swiss accent, looking directly into the camera with a confident smile: "Grüezi, ich bin von M P Carrosserie in Wolfwil. Wir kümmern uns um Carrosseriearbeiten, Lackierungen, Unfallreparaturen, Hagel- und Steinschlagschäden — alles aus einer Hand. Besuchen Sie uns auf www punkt mp carrosserie punkt c h." Premium cinematic look, ambient workshop atmosphere, warm color grading, natural breathing pauses. End with the warm "MP Carrosserie" name on screen.`;

export default function VideoStudio({ token }) {
  const [prompt, setPrompt] = useState(PRESET_PROMPT);
  const [size, setSize] = useState("720x1280");
  const [duration, setDuration] = useState(12);
  const [model, setModel] = useState("sora-2");
  const [label, setLabel] = useState("MP Carrosserie · Instagram Reel");
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const pollingRef = useRef(null);

  const loadJobs = async () => {
    try {
      const res = await axios.get(`${API}/video/list`);
      setJobs(res.data?.jobs || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  // Poll active jobs every 8s
  useEffect(() => {
    const hasPending = jobs.some(
      (j) => j.status === "queued" || j.status === "running"
    );
    clearInterval(pollingRef.current);
    if (hasPending) {
      pollingRef.current = setInterval(loadJobs, 8000);
    }
    return () => clearInterval(pollingRef.current);
  }, [jobs]);

  const submit = async () => {
    if (!token) {
      toast.error("Bitte zuerst einloggen");
      return;
    }
    if (!prompt.trim()) {
      toast.error("Prompt darf nicht leer sein");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/video/generate`, {
        prompt: prompt.trim(),
        size,
        duration,
        model,
        label: label.trim() || "Video",
        admin_token: token,
      });
      toast.success(
        "Generierung gestartet — die Erstellung dauert 3–8 Minuten."
      );
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Start");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteJob = async (id) => {
    if (!confirm("Video wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/video/${id}`, {
        params: { admin_token: token },
      });
      toast.success("Gelöscht");
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  return (
    <section className="bg-[#0e0e0e] border border-white/10 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-md bg-[#e52620] flex items-center justify-center">
          <Film className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
            Instagram-Reel generieren
            <span className="px-2 py-0.5 rounded bg-[#e52620]/15 text-[#e52620] text-[10px] uppercase tracking-[0.2em] font-mono">
              Sora 2
            </span>
          </h2>
          <p className="text-zinc-500 text-sm">
            9:16 vertikal · 4 / 8 / 12 Sekunden · Generierung dauert 3–8 Minuten
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
            Bezeichnung
          </label>
          <input
            data-testid="video-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-4 py-2.5 text-white placeholder-zinc-600 transition-colors"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
              Format
            </label>
            <select
              data-testid="video-size-select"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-3 py-2.5 text-white transition-colors text-sm"
            >
              <option value="720x1280">9:16 (720×1280)</option>
              <option value="1024x1792">9:16 HD (Pro · 1024×1792)</option>
              <option value="1280x720">16:9 (1280×720)</option>
              <option value="1024x1024">1:1 (1024×1024 · Pro)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
              Dauer
            </label>
            <select
              data-testid="video-duration-select"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value, 10))}
              className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-3 py-2.5 text-white transition-colors text-sm"
            >
              <option value={4}>4 Sek</option>
              <option value={8}>8 Sek</option>
              <option value={12}>12 Sek</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
              Modell
            </label>
            <select
              data-testid="video-model-select"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-3 py-2.5 text-white transition-colors text-sm"
            >
              <option value="sora-2">sora-2</option>
              <option value="sora-2-pro">sora-2-pro (HD)</option>
            </select>
          </div>
        </div>
      </div>

      <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
        Prompt (Englisch funktioniert am besten — Dialog auf Deutsch in den Anführungszeichen halten)
      </label>
      <textarea
        data-testid="video-prompt-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={9}
        className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-4 py-3 text-white placeholder-zinc-600 transition-colors resize-y text-sm font-mono leading-relaxed"
      />
      <button
        type="button"
        onClick={() => setPrompt(PRESET_PROMPT)}
        className="mt-2 text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1.5"
      >
        <RotateCw className="w-3 h-3" />
        Standard-Prompt zurücksetzen
      </button>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="video-generate-button"
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-3 bg-[#e52620] hover:bg-[#c10015] disabled:opacity-60 text-white font-bold uppercase tracking-[0.12em] text-sm px-7 py-4 rounded-md transition-all active:scale-95"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Starte…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Video generieren
            </>
          )}
        </button>
        <button
          onClick={loadJobs}
          data-testid="video-refresh-button"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2.5 rounded-md"
        >
          <RotateCw className="w-3.5 h-3.5" />
          Aktualisieren
        </button>
      </div>

      {/* Job list */}
      <div className="mt-10">
        <h3 className="text-sm uppercase tracking-[0.2em] font-mono text-zinc-400 mb-4">
          Aufträge ({jobs.length})
        </h3>
        {jobs.length === 0 ? (
          <div className="text-zinc-500 text-sm border border-dashed border-white/10 rounded-xl p-8 text-center">
            Noch keine Videos generiert.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                data-testid={`video-job-${job.id}`}
                className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden"
              >
                <div className="relative aspect-[9/16] bg-black/60 flex items-center justify-center">
                  {job.status === "done" && job.url ? (
                    <video
                      src={`${BACKEND}/api${job.url}`}
                      controls
                      playsInline
                      className="w-full h-full object-contain"
                    />
                  ) : job.status === "failed" ? (
                    <div className="text-center px-4">
                      <div className="text-[#e52620] font-bold text-sm uppercase tracking-[0.2em]">
                        Fehlgeschlagen
                      </div>
                      {job.error && (
                        <div className="mt-2 text-[10px] text-zinc-500 font-mono break-words">
                          {String(job.error).slice(0, 120)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-[#e52620] animate-spin mx-auto" />
                      <div className="mt-3 text-xs uppercase tracking-[0.2em] font-mono text-zinc-400">
                        {job.status === "queued" ? "In Warteschlange…" : "Wird generiert…"}
                      </div>
                      <div className="mt-1 text-[10px] text-zinc-600 font-mono">
                        3–8 Minuten
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-white font-bold text-sm tracking-tight truncate">
                    {job.label}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-500">
                    {job.size} · {job.duration}s · {job.model}
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[10px]">
                    <span
                      className={`px-2 py-1 rounded font-mono uppercase tracking-[0.2em] font-bold ${
                        job.status === "done"
                          ? "bg-emerald-500/15 text-emerald-400"
                          : job.status === "failed"
                          ? "bg-red-500/15 text-red-400"
                          : "bg-amber-500/15 text-amber-400"
                      }`}
                    >
                      {job.status}
                    </span>
                    <div className="flex items-center gap-2">
                      {job.status === "done" && job.url && (
                        <a
                          href={`${BACKEND}/api${job.url}`}
                          download={`${job.label || "video"}.mp4`}
                          className="inline-flex items-center gap-1 text-zinc-400 hover:text-white"
                          data-testid={`video-download-${job.id}`}
                        >
                          <Download className="w-3 h-3" />
                          MP4
                        </a>
                      )}
                      <button
                        onClick={() => deleteJob(job.id)}
                        data-testid={`video-delete-${job.id}`}
                        className="inline-flex items-center gap-1 text-zinc-500 hover:text-[#e52620]"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
